import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { GlareEffect } from '@sorare/core/src/atoms/animations/GlareEffect';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Grid } from '@sorare/core/src/atoms/layout/grid';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { HeadlineM } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';

import { BuyPackButton } from 'components/commonCard/BuyPackButton';

import type { FindItInCardPacks_cardPack } from './__generated__/index.graphql';

const CardPackWrapper = styled(Vertical)`
  position: relative;
  background: var(--c-nl-200);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-50);
  padding: var(--unit);
`;

const PackImg = styled(GlareEffect)`
  width: calc(18 * var(--unit));
  object-fit: contain;
  height: 100%;
`;

type Props = {
  cardPacks: FindItInCardPacks_cardPack[];
};

export const FindItInCardPacks = ({ cardPacks }: Props) => {
  const openDialog = useOpenDialog();
  if (!cardPacks.length) {
    return null;
  }

  return (
    <Vertical>
      <HeadlineM className="text-center">
        <FormattedMessage
          id="EmptyCollectionSlotContent.packs.title"
          defaultMessage="Try to find it in these packs:"
        />
      </HeadlineM>
      <Grid>
        {cardPacks.map(cardPack => (
          <CardPackWrapper key={cardPack.slug} center>
            <PackImg
              pictureUrl={proxyUrl(cardPack.pictureUrl, {
                cropWidth: 160,
              })}
            />

            <LinkOverlay
              as={BuyPackButton}
              color="primary"
              size="compact"
              cardPack={cardPack}
              onClick={() => {
                openDialog(DialogKey.cardPack, cardPack.slug);
              }}
            />
          </CardPackWrapper>
        ))}
      </Grid>
    </Vertical>
  );
};

FindItInCardPacks.fragments = {
  cardPack: gql`
    fragment FindItInCardPacks_cardPack on CardPack {
      slug
      pictureUrl
      currency
      price
      ...BuyPackButton_cardPack
    }
    ${BuyPackButton.fragments.cardPack}
  ` as TypedDocumentNode<FindItInCardPacks_cardPack>,
};
